import { useState, type CSSProperties, type FocusEvent } from "react";
import { Link } from "@/components/common/Link";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  MenuProvider,
  useMenu,
  type MenuItem,
} from "@/integrations/wordpress/WordPressMenuProvider";
import { assetUrl, homeUrl } from "@/lib/assets";

const navLinkStyle: CSSProperties = {
  color: "rgb(33, 33, 31)",
  fontFamily: "Switzer, sans-serif",
  fontSize: "14px",
  fontWeight: "500",
  textTransform: "uppercase",
  textDecoration: "none",
  letterSpacing: "0.01em",
  whiteSpace: "nowrap",
};

const defaultNavItems: MenuItem[] = [
  { id: "home", label: "INICIO", href: homeUrl() },
  { id: "about", label: "SOBRE", href: homeUrl("nosotros/") },
  { id: "beliefs", label: "CREENCIAS", href: homeUrl("creencias/") },
  {
    id: "ministries",
    label: "MINISTERIOS",
    href: homeUrl("ministerios/"),
    children: [
      { id: "mm33", label: "MM33", href: homeUrl("mm33/") },
      { id: "youth", label: "Red Pastoral Juvenil", href: homeUrl("red-pastoral-juvenil/") },
      { id: "next-gen", label: "Nuevas Generaciones", href: homeUrl("nuevas-generaciones/") },
      { id: "explorers", label: "Exploradores del Rey", href: homeUrl("exploradores-del-rey/") },
    ],
  },
  { id: "missions", label: "MISIONES", href: homeUrl("misiones/") },
  { id: "resources", label: "RECURSOS", href: homeUrl("recursos/") },
  { id: "donate", label: "DONAR", href: homeUrl("donar/") },
  { id: "contact", label: "CONTACTO", href: homeUrl("contacto/") },
];

function Chevron() {
  return (
    <svg
      width="8"
      height="5"
      viewBox="0 0 10 6"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 1L5 5L9 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DesktopMenuItem({ item }: { item: MenuItem }) {
  const [open, setOpen] = useState(false);
  const hasChildren = Boolean(item.children?.length);

  const closeAfterFocusLeaves = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setOpen(false);
    }
  };

  if (!hasChildren) {
    return (
      <Link
        to={item.href || "#"}
        className="nav-link-item"
        style={navLinkStyle}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <div
      className="dropdown-wrapper"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={closeAfterFocusLeaves}
    >
      <Link
        to={item.href || "#"}
        className="nav-link-item nav-parent-link"
        style={{ ...navLinkStyle, display: "flex", alignItems: "center", gap: "5px" }}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {item.label}
        <Chevron />
      </Link>

      {open && (
        <div className="dropdown-menu-custom">
          {item.children?.map((child) => (
            <Link key={child.id} to={child.href || "#"}>
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileMenuItem({ item }: { item: MenuItem }) {
  const [open, setOpen] = useState(false);
  const hasChildren = Boolean(item.children?.length);

  return (
    <div className="mobile-menu-item">
      <div className="mobile-menu-row">
        <Link to={item.href || "#"} className="mobile-menu-link">
          {item.label}
        </Link>

        {hasChildren && (
          <button
            type="button"
            className="mobile-submenu-toggle"
            aria-expanded={open}
            aria-label={`${open ? "Cerrar" : "Abrir"} submenú de ${item.label}`}
            onClick={() => setOpen((current) => !current)}
          >
            <span className={open ? "is-open" : ""}>
              <Chevron />
            </span>
          </button>
        )}
      </div>

      {hasChildren && open && (
        <div className="mobile-submenu">
          {item.children?.map((child) => (
            <Link key={child.id} to={child.href || "#"}>
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function HeaderContent() {
  const { menuItems } = useMenu();
  const navItems = menuItems.length > 0 ? menuItems : defaultNavItems;
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600&family=Inter:wght@300;400;500;600&display=swap');

        #header, #header * { box-sizing: border-box; }

        .utility-link:hover { color: #0057B8 !important; }

        .header-search-wrap { display: flex; align-items: center; gap: 8px; }
        .header-search-toggle {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 0;
          border: 0;
          background: transparent;
          color: #fff;
          cursor: pointer;
          font-family: Switzer, sans-serif;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: .05em;
          text-transform: uppercase;
        }
        .header-search-toggle:hover,.header-search-toggle:focus-visible { color: #75baff; outline: none; }
        .header-search-toggle img { width: 15px; height: 15px; filter: brightness(0) invert(1); }
        .header-search-form { display: flex; align-items: center; gap: 5px; }
        .header-search-form input {
          width: min(260px,25vw);
          height: 28px;
          padding: 0 12px;
          border: 1px solid rgba(255,255,255,.34);
          border-radius: 999px;
          outline: 0;
          background: rgba(255,255,255,.1);
          color: #fff;
          font: 500 12px/1 Switzer,sans-serif;
        }
        .header-search-form input::placeholder { color: rgba(255,255,255,.62); }
        .header-search-form input:focus { border-color: #75baff; background: rgba(255,255,255,.16); }
        .header-search-submit {
          display: grid;
          width: 28px;
          height: 28px;
          place-items: center;
          border: 0;
          border-radius: 50%;
          background: #fff;
          color: #21211f;
          cursor: pointer;
        }
        .header-search-submit img { width: 13px; height: 13px; }
        .header-search-close { padding: 2px 3px; border: 0; background: transparent; color: #fff; cursor: pointer; font-size: 18px; line-height: 1; }

        .mobile-utility-area { display: grid; gap: 10px; margin-top: 22px; padding-top: 20px; border-top: 1px solid #e2e2df; }
        .mobile-search-form { display: flex; gap: 7px; }
        .mobile-search-form input { min-width: 0; flex: 1; padding: 11px 13px; border: 1px solid #d2d2cf; border-radius: 999px; font: 500 13px/1 Switzer,sans-serif; }
        .mobile-search-form button { padding: 10px 15px; border: 0; border-radius: 999px; background: #21211f; color: #fff; font-size: 11px; font-weight: 800; text-transform: uppercase; }
        .mobile-events-link { display: block; padding: 11px 14px; border-radius: 999px; background: #eef6ff; color: #0057b8; font-size: 13px; font-weight: 800; text-align: center; text-decoration: none; text-transform: uppercase; }

        .nav-link-item {
          transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out;
          border-radius: 50px;
          padding: 8px 16px !important;
        }

        .nav-link-item:hover,
        .nav-link-item:focus-visible {
          color: #FFFFFF !important;
          background-color: #21211F !important;
          outline: none;
        }

        .social-icon { transition: opacity 0.15s ease-in-out; }
        .social-icon:hover { opacity: 0.7; }

        .dropdown-wrapper { position: relative; }

        .dropdown-menu-custom {
          position: absolute;
          top: 100%;
          left: 0;
          min-width: 230px;
          padding: 8px;
          z-index: 9999;
          background: white;
          border: 1px solid rgba(0, 87, 184, 0.16);
          border-radius: 12px;
          box-shadow: 0 12px 30px rgba(0,0,0,0.14);
        }

        .dropdown-menu-custom a {
          display: block;
          padding: 10px 14px;
          color: #21211F;
          font-size: 14px;
          font-weight: 600;
          text-decoration: none;
          font-family: Switzer, sans-serif;
          border-radius: 8px;
          white-space: nowrap;
        }

        .dropdown-menu-custom a:hover,
        .dropdown-menu-custom a:focus-visible {
          background: #EEF6FF;
          color: #0057B8;
          outline: none;
        }

        .mobile-menu-item { width: 100%; }
        .mobile-menu-row { display: flex; align-items: center; width: 100%; }

        .mobile-menu-link {
          flex: 1;
          color: #21211F;
          font-size: 16px;
          font-weight: 500;
          text-transform: uppercase;
          text-decoration: none;
          padding: 10px 16px;
          border-radius: 8px;
        }

        .mobile-menu-link:hover { color: #0057B8; background: #EEF6FF; }

        .mobile-submenu-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 42px;
          height: 42px;
          border: 0;
          border-radius: 8px;
          color: #21211F;
          background: transparent;
          cursor: pointer;
        }

        .mobile-submenu-toggle:hover { color: #0057B8; background: #EEF6FF; }
        .mobile-submenu-toggle span { transition: transform 0.2s ease; }
        .mobile-submenu-toggle span.is-open { transform: rotate(180deg); }

        .mobile-submenu {
          display: flex;
          flex-direction: column;
          padding: 0 0 6px 18px;
        }

        .mobile-submenu a {
          color: #353533;
          font-size: 14px;
          text-decoration: none;
          padding: 9px 14px;
          border-radius: 8px;
        }

        .mobile-submenu a:hover { color: #0057B8; background: #EEF6FF; }

        .header-main-shell {
          max-width: none !important;
          margin: 0 !important;
          padding-left: clamp(18px, 2vw, 38px) !important;
          padding-right: clamp(18px, 2vw, 38px) !important;
        }

        .header-main-navigation { margin-left: auto; }

        @media (max-width: 767px) {
          .header-page-container { padding-left: 18px !important; padding-right: 18px !important; }
        }
      `}</style>

      <div
        style={{
          backgroundColor: "#21211F",
          height: "38px",
          width: "100%",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          className="page-container header-page-container"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            maxWidth: "100%",
            marginLeft: 0,
            marginRight: 0,
            paddingLeft: "40px",
            paddingRight: "40px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
            <Link
              to="https://www.facebook.com/asambleasdedios.cr"
              newTab
              className="social-icon"
              style={{ color: "#FFFFFF", display: "flex", alignItems: "center" }}
              aria-label="Facebook"
            >
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </Link>

            <Link
              to="https://www.instagram.com/acad_costarica/"
              newTab
              className="social-icon"
              style={{ color: "#FFFFFF", display: "flex", alignItems: "center" }}
              aria-label="Instagram"
            >
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </Link>
          </div>

          <div className="hidden md:flex" style={{ alignItems: "center", gap: "24px" }}>
            <div className="header-search-wrap">
              {searchOpen ? (
                <form className="header-search-form" action={homeUrl()} method="get" role="search">
                  <label className="sr-only" htmlFor="acad-header-search">Buscar en el sitio</label>
                  <input id="acad-header-search" name="s" type="search" placeholder="¿Qué desea encontrar?" autoFocus required />
                  <button className="header-search-submit" type="submit" aria-label="Buscar">
                    <img src={assetUrl("icon-search.png")} alt="" />
                  </button>
                  <button className="header-search-close" type="button" aria-label="Cerrar búsqueda" onClick={() => setSearchOpen(false)}>×</button>
                </form>
              ) : (
                <button className="header-search-toggle" type="button" onClick={() => setSearchOpen(true)} aria-expanded="false">
                  Buscar
                  <span style={{ width: "1px", height: "12px", backgroundColor: "rgba(255,255,255,0.4)" }} />
                  <img src={assetUrl("icon-search.png")} alt="" />
                </button>
              )}
            </div>
            <Link to={homeUrl("eventos/")} className="utility-link" style={utilityLinkStyle}>EVENTOS</Link>
          </div>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#FFFFFF",
          height: "70px",
          width: "100%",
          display: "flex",
          alignItems: "center",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}
      >
        <div
          className="page-container header-page-container header-main-shell"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            maxWidth: "none",
            marginLeft: 0,
            marginRight: 0,
          }}
        >
          <Link
            to={homeUrl()}
            style={{ display: "flex", alignItems: "center", marginRight: "auto" }}
          >
            <img
              src={assetUrl("footer2.png")}
              alt="Asambleas de Dios Costa Rica"
              style={{ width: "84px", height: "auto", objectFit: "contain" }}
            />
          </Link>

          <nav className="header-main-navigation hidden lg:flex" aria-label="Navegación principal">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "1px solid rgba(0, 0, 0, 0.72)",
                borderRadius: "100px",
                padding: "6px 20px",
                gap: "4px",
              }}
            >
              {navItems.map((item) => (
                <DesktopMenuItem key={item.id} item={item} />
              ))}
            </div>
          </nav>

          <Sheet>
            <SheetTrigger asChild>
              <button
                className="lg:hidden"
                style={{
                  background: "none",
                  border: "1px solid rgba(0,0,0,0.2)",
                  borderRadius: "6px",
                  padding: "6px 10px",
                  cursor: "pointer",
                  color: "#21211F",
                }}
                aria-label="Abrir menú"
              >
                <svg width="22" height="18" viewBox="0 0 22 18" fill="none">
                  <path d="M1 1H21M1 9H21M1 17H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </SheetTrigger>

            <SheetContent side="right" style={{ fontFamily: "Switzer, sans-serif", width: "300px" }}>
              <SheetHeader>
                <SheetTitle>
                  <img
                    src={assetUrl("logo-color.png")}
                    alt="Asambleas de Dios Costa Rica"
                    style={{ width: "70px", height: "auto" }}
                  />
                </SheetTitle>
              </SheetHeader>

              <nav
                style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "24px" }}
                aria-label="Navegación móvil"
              >
                {navItems.map((item) => (
                  <MobileMenuItem key={item.id} item={item} />
                ))}
              </nav>

              <div className="mobile-utility-area">
                <form className="mobile-search-form" action={homeUrl()} method="get" role="search">
                  <label className="sr-only" htmlFor="acad-mobile-search">Buscar en el sitio</label>
                  <input id="acad-mobile-search" name="s" type="search" placeholder="Buscar en ACAD" required />
                  <button type="submit">Buscar</button>
                </form>
                <Link to={homeUrl("eventos/")} className="mobile-events-link">Ver eventos</Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </>
  );
}

const utilityLinkStyle: CSSProperties = {
  color: "#FFFFFF",
  fontSize: "13px",
  fontWeight: "500",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  textDecoration: "none",
  fontFamily: "Switzer, sans-serif",
  transition: "color 0.15s ease-in-out",
};

export default function Header() {
  return (
    <header
      id="header"
      style={{
        fontFamily: "Switzer, sans-serif",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1030,
        width: "100%",
      }}
    >
      <MenuProvider menu_id="primary">
        <HeaderContent />
      </MenuProvider>
    </header>
  );
}
